import { Box, Divider, FormControl, Grid, Paper, TextField, Switch, FormControlLabel } from '@mui/material'
import Typography from '@mui/material/Typography'
import React from 'react'
import { type BaseAddress, type CommitteePhone, type Country, type UsAddress, type Building } from 'services'
import { type State, UsStateSelect } from './state-select'
import { useBuildingLuts, BuildingIconButton, useUsStateLuts } from 'components'
import { applyBuildingInfo } from 'helpers'

export const PHONE_TYPE_ID = 1
export const FAX_TYPE_ID = 2
export interface UsAddressFieldsProps {
  comAddress: BaseAddress
  resetAddress: () => void
  updateAddress: (address: BaseAddress) => void
  startExpanded?: boolean
  showPhones?: boolean
  disabled?: boolean
  filterBuildings?: (buildings: Building[]) => Building[]
  showSecondaryBuildingButton?: boolean
}

export const UsAddressFields: React.FC<UsAddressFieldsProps> = ({
  comAddress,
  resetAddress,
  updateAddress,
  startExpanded = false,
  showPhones = true,
  disabled,
  filterBuildings,
  showSecondaryBuildingButton = false
}) => {
  const { lutValues: buildingLutValues, loading: buildingLoading } = useBuildingLuts()
  const { lutValues: stateLut, loading: stateLoading } = useUsStateLuts()
  const secondaryAddress = comAddress?.secondary
  const primaryAddress = comAddress?.primary
  const phone = comAddress?.phones?.find(phone => phone.typeId === PHONE_TYPE_ID && phone.displayOrder === 1)
  const fax = comAddress?.phones?.find(phone => phone.typeId === FAX_TYPE_ID && phone.displayOrder === 1)

  const filteredBuildings = filterBuildings != null ? filterBuildings(buildingLutValues.buildings) : []

  const handlePrimaryBuildingSelect = (building: Building): void => {
    updateAddress({
      ...comAddress,
      primary: applyBuildingInfo(building, primaryAddress, stateLut.states)
    })
  }

  const handleSecondaryBuildingSelect = (building: Building): void => {
    updateAddress({
      ...comAddress,
      secondary: applyBuildingInfo(building, secondaryAddress, stateLut.states)
    })
  }

  const changeSecondaryValue = (updatedAddress: UsAddress): void => {
    updateAddress({
      ...comAddress,
      secondary: updatedAddress
    })
  }

  const changePrimaryValue = (updatedAddress: UsAddress): void => {
    updateAddress({
      ...comAddress,
      primary: updatedAddress
    })
  }

  const handleChangeAddressTextField = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    filterKey: keyof UsAddress,
    isSecondary: boolean = false
  ): void => {
    const value = event.target.value
    if (isSecondary) {
      changeSecondaryValue({
        ...secondaryAddress,
        building: secondaryAddress?.building ?? '',
        room: secondaryAddress?.room ?? '',
        addressOne: secondaryAddress?.addressOne ?? '',
        addressTwo: secondaryAddress?.addressTwo ?? '',
        city: secondaryAddress?.city ?? '',
        state: secondaryAddress?.state ?? null,
        country: secondaryAddress?.country ?? null,
        zipCode: secondaryAddress?.zipCode ?? '',
        [filterKey]: value
      })
    } else {
      changePrimaryValue({
        ...primaryAddress,
        building: primaryAddress?.building ?? '',
        room: primaryAddress?.room ?? '',
        addressOne: primaryAddress?.addressOne ?? '',
        addressTwo: primaryAddress?.addressTwo ?? '',
        city: primaryAddress?.city ?? '',
        state: primaryAddress?.state ?? null,
        country: primaryAddress?.country ?? null,
        zipCode: primaryAddress?.zipCode ?? '',
        [filterKey]: value
      })
    }
  }

  const renderAddressSection = (address: UsAddress, isSecondary: boolean): JSX.Element => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        {renderAddressTextField('Building', 'building', address.building, isSecondary)}
      </Grid>
      <Grid item xs={6}>
        {renderAddressTextField('Room', 'room', address.room, isSecondary)}
      </Grid>
      <Grid item xs={6}>
        {renderAddressTextField('Address 1', 'addressOne', address.addressOne, isSecondary)}
      </Grid>
      <Grid item xs={6}>
        {renderAddressTextField('Address 2', 'addressTwo', address.addressTwo, isSecondary)}
      </Grid>
      <Grid item xs={5}>
        {renderAddressTextField('City', 'city', address.city, isSecondary)}
      </Grid>
      <Grid item xs={4}>
        {renderSelectState(address.state, isSecondary, address.country)}
      </Grid>
      <Grid item xs={3}>
        {renderAddressTextField('Postal Code', 'zipCode', address.zipCode, isSecondary)}
      </Grid>
    </Grid>
  )

  const renderSelectState = (
    state: State | null,
    isSecondary: boolean = false,
    country: Country | null
  ): JSX.Element => (
    <FormControl variant="outlined" fullWidth={true}>
      <UsStateSelect disabled={disabled ?? true} value={state} updateState={(e) => {
        if (!isSecondary) {
          changePrimaryValue({
            ...primaryAddress,
            building: primaryAddress?.building ?? '',
            room: primaryAddress?.room ?? '',
            addressOne: primaryAddress?.addressOne ?? '',
            addressTwo: primaryAddress?.addressTwo ?? '',
            city: primaryAddress?.city ?? '',
            state: e,
            country: primaryAddress?.country ?? null,
            zipCode: primaryAddress?.zipCode ?? ''
          })
        } else {
          changeSecondaryValue({
            ...secondaryAddress,
            building: secondaryAddress?.building ?? '',
            room: secondaryAddress?.room ?? '',
            addressOne: secondaryAddress?.addressOne ?? '',
            addressTwo: secondaryAddress?.addressTwo ?? '',
            city: secondaryAddress?.city ?? '',
            state: e,
            country: secondaryAddress?.country ?? null,
            zipCode: secondaryAddress?.zipCode ?? ''
          })
        }
      }}/>
    </FormControl>
  )

  const renderAddressTextField = (
    label: string,
    filterKey: keyof UsAddress,
    value: string | null = '',
    isSecondary: boolean = false
  ): JSX.Element => (
    <FormControl variant="outlined" fullWidth={true}>
      <TextField
        label={label}
        variant="outlined"
        disabled={disabled}
        value={value ?? ''}
        onChange={(e) => { handleChangeAddressTextField(e, filterKey, isSecondary) }}
        fullWidth
      />
    </FormControl>
  )

  const handlePhoneChange = (fax: boolean, field: keyof CommitteePhone, value: string): void => {
    const updatedPhones = comAddress.phones.map(phone => {
      if (phone.typeId === (!fax ? PHONE_TYPE_ID : FAX_TYPE_ID) && phone.displayOrder === 1) {
        return { ...phone, [field]: value }
      }
      return phone
    })
    updateAddress({ ...comAddress, phones: updatedPhones })
  }

  const handleSwitchClick = (value: 1 | 2): void => {
    updateAddress({ ...comAddress, mailingAddress: value })
  }

  return (
    <Grid container justifyContent={'flex-start'}>
  {comAddress.phones != null && comAddress.phones.length > 0 && showPhones && (
      <>
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ pb: 1, mb: 2, pt: 1 }}>
            <Grid container spacing={2} justifyContent={'flex-start'}>
              <Grid item xs={0.2}/>
              {phone != null && (
                <>
                  <Grid item xs={'auto'} display={'flex'} alignItems={'center'}>
                    <Typography sx={{ fontWeight: 'bold' }} color={'primary'} noWrap>Phone:</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Area code"
                      variant="outlined"
                      disabled={disabled}
                      value={phone.areaCode}
                      onChange={(e) => { handlePhoneChange(false, 'areaCode', e.target.value) }}
                      inputProps={{ maxLength: 3 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs>
                    <TextField
                      label="Phone number"
                      variant="outlined"
                      disabled={disabled}
                      value={phone.phoneNumber}
                      onChange={(e) => { handlePhoneChange(false, 'phoneNumber', e.target.value) }}
                      inputProps={{ maxLength: 10 }}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              {fax != null && (
                <>
                  <Grid item xs={'auto'} display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
                    <Typography sx={{ fontWeight: 'bold' }} color={'primary'} noWrap>Fax:</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Area code"
                      variant="outlined"
                      disabled={disabled}
                      value={fax.areaCode}
                      onChange={(e) => { handlePhoneChange(true, 'areaCode', e.target.value) }}
                      inputProps={{ maxLength: 3 }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs>
                    <TextField
                      label="Fax number"
                      variant="outlined"
                      disabled={disabled}
                      value={fax.phoneNumber}
                      onChange={(e) => { handlePhoneChange(true, 'phoneNumber', e.target.value) }}
                      inputProps={{ maxLength: 10 }}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        </Grid></>)}
      <Grid item xs={5.95}>
        <Box display="flex" justifyContent="space-between" alignItems="center" paddingBottom={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color={'secondary.dark'} sx={{ fontWeight: 'bold' }}>Primary:</Typography>
            {filterBuildings != null && <BuildingIconButton buildings={filteredBuildings} onBuildingSelect={handlePrimaryBuildingSelect} disabled={disabled} loading={buildingLoading || stateLoading} />
            }
          </Box>
          <FormControlLabel
            control={
              <Switch
                disabled={disabled}
                checked={comAddress.mailingAddress === 1}
                onChange={() => {
                  handleSwitchClick(1)
                }}
              />
            }
            label="Mailing Address"
          />
        </Box>
        <FormControl fullWidth>
          {(primaryAddress != null) && renderAddressSection(primaryAddress, false)}
        </FormControl>
      </Grid>
      <Grid item xs={0.1}>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <Divider orientation="vertical" flexItem />
        </Box>
      </Grid>
      <Grid item xs={5.95}>
        <Box display="flex" justifyContent="space-between" alignItems="center" paddingBottom={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color={'secondary.dark'} sx={{ fontWeight: 'bold' }}>Secondary:</Typography>
            {showSecondaryBuildingButton && filterBuildings != null && <BuildingIconButton buildings={filteredBuildings} onBuildingSelect={handleSecondaryBuildingSelect} disabled={disabled} loading={buildingLoading || stateLoading} />
            }
          </Box>
          <FormControlLabel
            control={
              <Switch
                disabled={disabled}
                checked={comAddress.mailingAddress === 2}
                onChange={() => {
                  handleSwitchClick(2)
                }}
              />
            }
            label="Mailing Address"
          />
        </Box>
        <FormControl fullWidth>
          {(secondaryAddress != null) && renderAddressSection(secondaryAddress, true)}
        </FormControl>
      </Grid>
    </Grid>
  )
}
